import styled from 'styled-components';
import { forwardRef } from 'react';
import { disabledState } from '@/styles/mixins';
import { InputProps } from '@/Models';

// Input wrapper that doesn't pass invalid props to DOM
const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    $label, 
    $error, 
    $helperText, 
    $hasError, 
    $disabled, 
    className,
    ...props 
  }, ref) => {
    const inputId = props.id;
    
    return (
      <InputContainer className={className} $disabled={$disabled}>
        {$label && (
          <Label htmlFor={inputId}>
            {$label}
          </Label>
        )}
        
        <InputWrapper $hasError={$hasError || !!$error}>
          <StyledInput
            ref={ref}
            id={inputId}
            $hasError={$hasError || !!$error}
            disabled={$disabled}
            {...props}
          />
        </InputWrapper>
        
        {$error ? (
          <ErrorMessage>{$error}</ErrorMessage>
        ) : $helperText ? (
          <HelperText>{$helperText}</HelperText>
        ) : null}
      </InputContainer>
    );
  }
);

Input.displayName = 'Input';

export { Input };

// Styled components
const InputContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.xs}; /* 12px - Premium spec */
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal}; /* 400 */
  color: ${({ theme }) => theme.colors.text.label};
  margin-bottom: 2px;
  display: block;
`;

/* Premium Input Container - wraps the input with border */
const InputWrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.inputColor};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.grayLight};
  border-radius: 0.5rem; /* 8px - Premium container spec */
  box-shadow: none;
  padding: 0.5rem; /* Premium spec - 8px padding on container */
  transition: border-color ${({ theme }) => theme.transitions.fast};
  

`;

const StyledInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  font-family: ${({ theme }) => theme.typography.fontFamily}; /* Poppins */
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal}; /* 400 */
  border-radius: 0.25rem; /* 4px */
  outline: none;
  padding: 0.75rem; /* 12px - Premium spec */
  height: 27px; /* Premium spec */
  box-shadow: none;
  border: none; /* Border is on container */
  -webkit-tap-highlight-color: transparent;
  color: ${({ theme }) => theme.colors.text.inputColor};
  font-size: 1rem; /* 16px - Premium spec */
  background-color: ${({ theme }) => theme.colors.bgPrimary};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
  }
  
  ${disabledState}
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const ErrorMessage = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.error};
`;

const HelperText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray};
`;